import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Send } from "lucide-react";

// Public lead-capture form — no auth required. Cold callers get this
// URL from admin, fill it, and the lead lands in the pre-configured
// CRM board/column. Owner ask 2026-08-27.

export default function PublicLeadCapture() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [f, setF] = useState({
    name: "", phone: "", email: "", property_address: "",
    motivation: "", motivation_level: "warm",
    timeline_bucket: "", asking_price: "", notes: "",
  });

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    axios.get(`${API}/api/public/lead-capture/${token}`)
      .then(({ data }) => setMeta(data))
      .catch(() => setMeta({ error: true }));
  }, [token, API]);

  const submit = async () => {
    if (!f.name && !f.phone) return toast.error("Name or phone required");
    setBusy(true);
    try {
      await axios.post(`${API}/api/public/lead-capture/${token}`, f);
      setSent(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  if (meta?.error) {
    return <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md text-center">
        <div className="text-2xl font-bold text-red-600 mb-2">Link not found</div>
        <div className="text-sm text-slate-600">This lead-capture link is invalid or has been disabled.</div>
      </div>
    </div>;
  }
  if (sent) {
    return <div className="min-h-screen flex items-center justify-center p-6 bg-emerald-50">
      <div className="max-w-md text-center bg-white rounded-2xl border-2 border-emerald-200 p-8">
        <CheckCircle2 size={48} className="mx-auto text-emerald-500 mb-3" />
        <div className="text-2xl font-bold text-emerald-800 mb-2">Lead Submitted!</div>
        <div className="text-sm text-slate-600 mb-4">Your lead is now in the FlipPilot pipeline. The team will follow up.</div>
        <Button onClick={() => { setSent(false); setF({ name:"",phone:"",email:"",property_address:"",motivation:"",motivation_level:"warm",timeline_bucket:"",asking_price:"",notes:"" }); }}>
          Submit another
        </Button>
      </div>
    </div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <div className="max-w-2xl mx-auto space-y-4" data-testid="public-capture-page">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-slate-800">Submit a Seller Lead</h1>
          {meta?.label && <div className="text-sm text-fuchsia-600 font-medium mt-1">→ {meta.label}</div>}
        </div>
        <div className="rounded-2xl border-2 border-slate-200 bg-white p-5 space-y-3 shadow-sm">
          <Field label="Seller name *" value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="capture-name" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone *" value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testid="capture-phone" />
            <Field label="Email" value={f.email} onChange={(v) => setF({ ...f, email: v })} testid="capture-email" />
          </div>
          <Field label="Property address" value={f.property_address} onChange={(v) => setF({ ...f, property_address: v })} testid="capture-address" />
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Why is the seller selling?</Label>
            <Textarea rows={3} value={f.motivation} onChange={(e) => setF({ ...f, motivation: e.target.value })} className="mt-1" data-testid="capture-motivation" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Level</Label>
              <select value={f.motivation_level} onChange={(e) => setF({ ...f, motivation_level: e.target.value })}
                      className="mt-1 w-full border-2 border-slate-200 rounded-md px-3 py-2 text-sm">
                <option value="hot">🔥 Hot</option>
                <option value="warm">Warm</option>
                <option value="cold">Cold</option>
              </select>
            </div>
            <Field label="Timeline" value={f.timeline_bucket} onChange={(v) => setF({ ...f, timeline_bucket: v })} placeholder="ASAP" />
            <Field label="Asking $" value={f.asking_price} onChange={(v) => setF({ ...f, asking_price: v })} placeholder="225000" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Notes</Label>
            <Textarea rows={4} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}
                      placeholder="What did the seller say?" className="mt-1" data-testid="capture-notes" />
          </div>
          <Button onClick={submit} disabled={busy} className="w-full bg-fuchsia-600 hover:bg-fuchsia-700"
            data-testid="capture-submit-btn">
            {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <Send size={16} className="mr-2" />}
            Submit Lead
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-500 font-bold">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
             className="mt-1" data-testid={testid} />
    </div>
  );
}
